import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, ArrowLeft, Copy } from "lucide-react";

const EMPTY = {
  customer_type: "individual",
  title: "", first_name: "", middle_name: "", last_name: "",
  date_of_birth: "", gender: "", nationality: "",
  mobile: "", alt_mobile: "", whatsapp: "", email: "", alt_email: "",
  address: "", city: "", state: "", country: "", pincode: "",
  company_name: "", company_gstin: "", company_pan: "", company_address: "",
  billing_contact: "", billing_email: "", billing_mobile: "",
  preferred_destinations: [], preferred_hotel_category: "", preferred_room_category: "",
  preferred_meal_plan: "", preferred_airline: "", seat_preference: "", special_requirements: "",
  passport_number: "", passport_expiry: "", pan_number: "", other_id_type: "", other_id_number: "",
  status: "active",
};

const TITLES = ["Mr.", "Mrs.", "Ms.", "Dr."];
const GENDERS = ["Male", "Female", "Other", "Prefer not to say"];
const HOTEL_CATS = ["3 Star", "4 Star", "5 Star", "Luxury / 5★+"];
const ROOM_CATS = ["Standard Room", "Superior Room", "Deluxe Room", "Premium Room", "Executive Room", "Junior Suite", "Suite", "Family Room", "Villa"];
const MEAL_PLANS = ["Room Only (RO)", "Bed & Breakfast (BB)", "Half Board (HB)", "Full Board (FB)", "All Inclusive (AI)"];
const SEAT_PREFS = ["Window", "Aisle", "Middle", "Emergency Exit", "No preference"];

export default function CustomerForm() {
  const nav = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [saveAnother, setSaveAnother] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/customers/${id}`).then(({ data }) => {
      setForm({ ...EMPTY, ...data, preferred_destinations: data.preferred_destinations || [] });
    }).catch(() => { toast.error("Could not load customer"); nav("/customers"); });
  }, [id]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isCorporate = form.customer_type === "corporate";

  const submit = async () => {
    if (!form.first_name && !form.company_name) {
      toast.error("First name or company name is required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/customers/${id}`, form);
        toast.success("Customer updated");
        nav(`/customers/${id}`);
      } else {
        const { data } = await api.post("/customers", form);
        toast.success(`Customer created: ${data.customer_id}`);
        if (saveAnother) { setForm(EMPTY); setSaveAnother(false); }
        else nav(`/customers/${data.id}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.existing_id) {
        toast.error(`${detail.message}: ${detail.existing_customer_id} · ${detail.existing_name}`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Save failed");
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <Sidebar />
      <main className="flex-1">
        <header className="bg-white border-b border-border">
          <div className="max-w-6xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button onClick={() => nav(-1)} className="text-slate-500 hover:text-slate-900" data-testid="back-btn">
                <ArrowLeft size={16} />
              </button>
              <div>
                <div className="overline text-[10px] tracking-widest text-slate-500">CUSTOMER MANAGEMENT</div>
                <h1 className="font-editorial text-3xl mt-1">{isEdit ? "Edit Customer" : "New Customer"}</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isEdit && (
                <button onClick={() => { setSaveAnother(true); submit(); }}
                        disabled={saving} data-testid="save-and-new-btn"
                        className="text-xs px-3 py-2 border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50">
                  <Copy size={12} className="inline mr-1" /> Save & Create Another
                </button>
              )}
              <button onClick={submit} disabled={saving} data-testid="save-btn"
                      className="btn-bronze inline-flex items-center gap-2 text-sm">
                <Save size={14} /> {saving ? "Saving…" : "Save Customer"}
              </button>
            </div>
          </div>
        </header>

        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8 space-y-6">
          {/* Type toggle */}
          <Card title="Customer Type">
            <div className="flex gap-3">
              {["individual", "corporate"].map((t) => (
                <button key={t} data-testid={`type-${t}`}
                        onClick={() => set("customer_type", t)}
                        className={`px-4 py-2 text-sm rounded border ${form.customer_type === t
                          ? "bg-emerald-600 border-emerald-600 text-white"
                          : "bg-white border-slate-300 text-slate-700 hover:border-slate-400"}`}>
                  {t === "individual" ? "Individual" : "Corporate"}
                </button>
              ))}
            </div>
          </Card>

          {/* Basic */}
          <Card title="Basic Details">
            <Grid>
              <SelectField label="Title" value={form.title} onChange={(v) => set("title", v)} options={TITLES} testid="f-title" />
              <TextField label="First Name *" value={form.first_name} onChange={(v) => set("first_name", v)} testid="f-first" />
              <TextField label="Middle Name" value={form.middle_name} onChange={(v) => set("middle_name", v)} testid="f-middle" />
              <TextField label="Last Name" value={form.last_name} onChange={(v) => set("last_name", v)} testid="f-last" />
              <TextField label="Date of Birth" type="date" value={form.date_of_birth} onChange={(v) => set("date_of_birth", v)} testid="f-dob" />
              <SelectField label="Gender" value={form.gender} onChange={(v) => set("gender", v)} options={GENDERS} testid="f-gender" />
              <TextField label="Nationality" value={form.nationality} onChange={(v) => set("nationality", v)} testid="f-nat" />
            </Grid>
          </Card>

          {/* Contact */}
          <Card title="Contact Details">
            <Grid>
              <TextField label="Mobile Number" value={form.mobile} onChange={(v) => set("mobile", v)} testid="f-mobile" />
              <TextField label="Alternate Mobile" value={form.alt_mobile} onChange={(v) => set("alt_mobile", v)} testid="f-alt-mobile" />
              <TextField label="WhatsApp Number" value={form.whatsapp} onChange={(v) => set("whatsapp", v)} testid="f-wa" />
              <TextField label="Email" value={form.email} onChange={(v) => set("email", v)} testid="f-email" />
              <TextField label="Alternate Email" value={form.alt_email} onChange={(v) => set("alt_email", v)} testid="f-alt-email" />
            </Grid>
          </Card>

          {/* Address */}
          <Card title="Address">
            <Grid>
              <TextField label="Address" value={form.address} onChange={(v) => set("address", v)} testid="f-addr" wide />
              <TextField label="City" value={form.city} onChange={(v) => set("city", v)} testid="f-city" />
              <TextField label="State" value={form.state} onChange={(v) => set("state", v)} testid="f-state" />
              <TextField label="Country" value={form.country} onChange={(v) => set("country", v)} testid="f-country" />
              <TextField label="PIN / ZIP" value={form.pincode} onChange={(v) => set("pincode", v)} testid="f-pin" />
            </Grid>
          </Card>

          {/* Corporate */}
          {isCorporate && (
            <Card title="Corporate Details">
              <Grid>
                <TextField label="Company Name *" value={form.company_name} onChange={(v) => set("company_name", v)} testid="f-co-name" />
                <TextField label="Company GSTIN" value={form.company_gstin} onChange={(v) => set("company_gstin", v)} testid="f-co-gstin" />
                <TextField label="Company PAN" value={form.company_pan} onChange={(v) => set("company_pan", v)} testid="f-co-pan" />
                <TextField label="Company Address" value={form.company_address} onChange={(v) => set("company_address", v)} testid="f-co-addr" wide />
                <TextField label="Billing Contact Person" value={form.billing_contact} onChange={(v) => set("billing_contact", v)} testid="f-bill-contact" />
                <TextField label="Billing Email" value={form.billing_email} onChange={(v) => set("billing_email", v)} testid="f-bill-email" />
                <TextField label="Billing Mobile" value={form.billing_mobile} onChange={(v) => set("billing_mobile", v)} testid="f-bill-mobile" />
              </Grid>
            </Card>
          )}

          {/* Travel prefs */}
          <Card title="Travel Preferences">
            <Grid>
              <TextField label="Preferred Destinations (comma separated)"
                value={(form.preferred_destinations || []).join(", ")}
                onChange={(v) => set("preferred_destinations", v.split(",").map((s) => s.trim()).filter(Boolean))}
                testid="f-pref-dest" wide />
              <SelectField label="Preferred Hotel Category" value={form.preferred_hotel_category} onChange={(v) => set("preferred_hotel_category", v)} options={HOTEL_CATS} testid="f-pref-hotel" />
              <SelectField label="Preferred Room Category" value={form.preferred_room_category} onChange={(v) => set("preferred_room_category", v)} options={ROOM_CATS} testid="f-pref-room" />
              <SelectField label="Preferred Meal Plan" value={form.preferred_meal_plan} onChange={(v) => set("preferred_meal_plan", v)} options={MEAL_PLANS} testid="f-pref-meal" />
              <TextField label="Preferred Airline" value={form.preferred_airline} onChange={(v) => set("preferred_airline", v)} testid="f-pref-airline" />
              <SelectField label="Seat Preference" value={form.seat_preference} onChange={(v) => set("seat_preference", v)} options={SEAT_PREFS} testid="f-seat" />
              <div className="col-span-full">
                <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Special Requirements / Notes</label>
                <textarea data-testid="f-notes" value={form.special_requirements} onChange={(e) => set("special_requirements", e.target.value)}
                          rows={3} className="w-full border border-border rounded px-3 py-2 text-sm" />
              </div>
            </Grid>
          </Card>

          {/* Documents */}
          <Card title="Documents & ID">
            <Grid>
              <TextField label="Passport Number" value={form.passport_number} onChange={(v) => set("passport_number", v)} testid="f-pp" />
              <TextField label="Passport Expiry" type="date" value={form.passport_expiry} onChange={(v) => set("passport_expiry", v)} testid="f-pp-exp" />
              <TextField label="PAN Number" value={form.pan_number} onChange={(v) => set("pan_number", v)} testid="f-pan" />
              <TextField label="Other ID Type" value={form.other_id_type} onChange={(v) => set("other_id_type", v)} testid="f-other-idt" />
              <TextField label="Other ID Number" value={form.other_id_number} onChange={(v) => set("other_id_number", v)} testid="f-other-idn" />
            </Grid>
            <div className="text-xs text-slate-400 mt-3">Document upload coming next iteration.</div>
          </Card>
        </div>
      </main>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="bg-white border border-border rounded p-5">
      <h2 className="text-sm font-semibold text-emerald-700 mb-4 uppercase tracking-widest">{title}</h2>
      {children}
    </section>
  );
}
function Grid({ children }) { return <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{children}</div>; }
function TextField({ label, value, onChange, type = "text", testid, wide = false }) {
  return (
    <div className={wide ? "md:col-span-3" : ""}>
      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
      <input data-testid={testid} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
             className="w-full border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
    </div>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
      <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
              className="w-full border border-border rounded px-3 py-2 text-sm bg-white">
        <option value="">—</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
